'use client'
import { cn } from '@/lib/utils'
import { Product } from '@/types/product'
import { useTranslations } from 'next-intl'
import { useProductSizes } from '../_hooks/use-product-sizes'

interface Props {
  sizeChart: string
  product: Product
  colorSelected: string | null
  onSizeSelect: (size: string) => void
  selectedSize: string | null
}

interface SizeItemProps {
  isActive?: boolean
  onClick?: () => void
  isDisabled?: boolean
  title: string
}

const SizeItem = ({ isActive, onClick, isDisabled, title }: SizeItemProps) => {
  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          'relative h-9 w-13 rounded-xl bg-neutral-100 p-2 font-sans text-base leading-5 text-neutral-600 hover:bg-neutral-900 hover:text-light lg:h-11 lg:w-17.5 lg:bg-neutral-300',
          isDisabled &&
            'pointer-events-none cursor-not-allowed !bg-neutral-100/50 !text-neutral-300 before:absolute before:left-0 before:top-1/2 before:w-full before:rotate-[30deg] before:border-t before:border-dashed before:border-neutral-300 after:absolute after:left-0 after:top-1/2 after:w-full after:rotate-[145deg] after:border-t after:border-dashed after:border-neutral-300 lg:bg-neutral-100/50',
          isActive && 'bg-neutral-900 text-light lg:bg-neutral-900'
        )}
      >
        {title}
      </button>
    </li>
  )
}

export const ProductInfoSizes: React.FC<Props> = ({
  sizeChart,
  product,
  colorSelected,
  onSizeSelect,
  selectedSize,
}) => {
  const t = useTranslations('landing_wowbox.sizes')
  const { sizeOptions, sizeSelected, isDisabled, handleSizeClick } =
    useProductSizes({
      product,
      colorSelected,
      selectedSize,
      onSizeSelect,
    })

  if (
    !sizeOptions?.length ||
    (sizeOptions.length === 1 && sizeOptions[0] === '')
  ) {
    return null
  }

  return (
    <div>
      <div className='mb-2 flex items-baseline justify-between lg:py-0'>
        <span className='font-sans text-base leading-5 text-neutral-900 max-lg:hidden'>
          {t('size')}:{' '}
          <p className='inline text-neutral-600'> {sizeSelected}</p>
        </span>
        {sizeChart && (
          <div className='text-sm text-neutral-600'>
            {/* Size chart implementation could go here */}
          </div>
        )}
      </div>

      <ul className='flex flex-wrap gap-3 lg:gap-4 lg:gap-y-3'>
        {sizeOptions.map((sizeOption: string, index: number) => (
          <SizeItem
            key={index}
            isActive={sizeSelected === sizeOption}
            onClick={() => handleSizeClick(sizeOption)}
            title={sizeOption}
            isDisabled={isDisabled(sizeOption)}
          />
        ))}
      </ul>
    </div>
  )
}
